import { useCallback, useEffect, useState } from 'react'

export type ThemeChoice = 'light' | 'dark'

const STORAGE_KEY = 'moms-console-theme'

/**
 * 시안과 같은 규칙: data-theme 이 없으면 OS 선호를 현재값으로 간주하고 반전한다.
 * 시안과 다른 점은 선택을 localStorage 에 남기는 것 하나다 — 온콜 중 새로고침이 잦다.
 */
export const resolveNextTheme = (current: string | null, prefersDark: boolean): ThemeChoice => {
  if (current === 'dark') return 'light'
  if (current === 'light') return 'dark'
  return prefersDark ? 'light' : 'dark'
}

const readStored = (): ThemeChoice | null => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v === 'dark' || v === 'light' ? v : null
  } catch {
    return null
  }
}

const prefersDark = (): boolean => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false

export const useTheme = (): { theme: ThemeChoice; toggle: () => void } => {
  const [theme, setTheme] = useState<ThemeChoice>(() => readStored() ?? (prefersDark() ? 'dark' : 'light'))

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try {
      window.localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      /* 사파리 프라이빗 모드 등 — 테마는 남지 않아도 화면은 돌아야 한다 */
    }
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((prev) => resolveNextTheme(prev, prefersDark()))
  }, [])

  return { theme, toggle }
}
